import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { useModalStore } from '@/stores/modal'
import { requireAuth, requireAdmin } from './guards'

// Importar vistas usando aliases
import Home from '@/views/Home.vue'
import Products from '@/views/Products.vue'
import About from '@/views/About.vue'
import ProductDetail from '@/views/product/ProductDetail.vue'
import Admin from '@/views/admin/Admin.vue'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: Home,
    meta: {
      title: 'Home - SupplemEarth | Premium Supplements'
    }
  },
  {
    path: '/products',
    name: 'products',
    component: Products,
    alias: '/productos', // Mantener alias para compatibilidad
    meta: {
      title: 'Products - SupplemEarth | Premium Supplements'
    }
  },
  {
    path: '/product/:id',
    name: 'product-detail',
    component: ProductDetail,
    alias: '/producto/:id', // Mantener alias para compatibilidad
    meta: {
      title: 'Product Details - SupplemEarth'
    }
  },
  {
    path: '/about',
    name: 'about',
    component: About,
    meta: {
      title: 'About Us - SupplemEarth'
    }
  },
  {
    path: '/checkout',
    name: 'checkout',
    component: () => import('@/views/Checkout.vue'),
    meta: {
      title: 'Checkout - SupplemEarth',
      requiresAuth: true
    },
    beforeEnter: requireAuth
  },
  {
    path: '/checkout/success',
    name: 'checkout-success',
    component: () => import('@/views/CheckoutSuccess.vue'),
    meta: {
      title: 'Order Confirmed - SupplemEarth'
    }
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('@/views/Profile.vue'),
    meta: {
      title: 'My Profile - SupplemEarth',
      requiresAuth: true
    },
    beforeEnter: requireAuth
  },
  {
    path: '/orders',
    name: 'my-orders',
    component: () => import('@/views/MyOrders.vue'),
    meta: {
      title: 'My Orders - SupplemEarth',
      requiresAuth: true
    },
    beforeEnter: requireAuth
  },
  {
    path: '/terms',
    name: 'terms',
    component: () => import('@/views/TermsAndConditions.vue'),
    meta: {
      title: 'Terms and Conditions - SupplemEarth'
    }
  },
  {
    path: '/admin',
    component: Admin,
    redirect: '/admin/dashboard',
    meta: {
      hideLayout: true,
      requiresAuth: true,
      requiresAdmin: true
    },
    beforeEnter: requireAdmin,
    children: [
      {
        path: 'dashboard',
        name: 'admin-dashboard',
        component: () => import('@/views/admin/AdminDashboard.vue'),
        meta: {
          title: 'Dashboard - SupplemEarth Admin',
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: 'products',
        name: 'admin-products',
        component: () => import('@/views/admin/AdminProducts.vue'),
        meta: {
          title: 'Products - SupplemEarth Admin',
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: 'orders',
        name: 'admin-orders',
        component: () => import('@/views/admin/AdminOrders.vue'),
        meta: {
          title: 'Orders - SupplemEarth Admin',
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: 'users',
        name: 'admin-users',
        component: () => import('@/views/admin/AdminUsers.vue'),
        meta: {
          title: 'Users - SupplemEarth Admin',
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: 'analytics',
        name: 'admin-analytics',
        component: () => import('@/views/admin/AdminDashboard.vue'), // Placeholder
        meta: {
          title: 'Analytics - SupplemEarth Admin',
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: 'inventory',
        name: 'admin-inventory',
        component: () => import('@/views/admin/AdminDashboard.vue'), // Placeholder
        meta: {
          title: 'Inventory - SupplemEarth Admin',
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: 'settings',
        name: 'admin-settings',
        component: () => import('@/views/admin/AdminDashboard.vue'), // Placeholder
        meta: {
          title: 'Settings - SupplemEarth Admin',
          requiresAuth: true,
          requiresAdmin: true
        }
      }
    ]
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/ForgotPassword.vue'),
    meta: {
      title: 'Forgot Password - SupplemEarth',
      hideLayout: true
    }
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/views/ResetPassword.vue'),
    meta: {
      title: 'Reset Password - SupplemEarth',
      hideLayout: true
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    redirect: '/'
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // Si es la misma ruta (ej. clic en "Terms" estando en /terms), no hacer scroll
    if (to.path === from.path) {
      return
    }
    return { top: 0, behavior: 'smooth' }
  }
})

// Navigation guard para títulos de página y verificación de autenticación
router.beforeEach(async (to, _from, next) => {
  const userStore = useUserStore()
  
  // Inicializar usuario si no está inicializado
  if (!userStore.isAuthenticated && localStorage.getItem('token')) {
    userStore.initUser()
  }
  
  // Si el usuario está autenticado, validar el token antes de continuar
  if (userStore.isAuthenticated) {
    const isValid = userStore.validateToken()
    if (!isValid) {
      // Token inválido, redirigir al home
      next({ name: 'home', query: { expired: 'true' } })
      return
    }
  }
  
  // Verificar rutas protegidas
  if (to.meta.requiresAuth && !userStore.isAuthenticated) {
    // Si es checkout, abrir el modal de login
    if (to.name === 'checkout') {
      const modalStore = useModalStore()
      modalStore.openLoginModal()
      next({ name: 'home', query: { redirect: to.fullPath } })
      return
    }
    // Para otras rutas protegidas, solo redirigir
    next({
      name: 'home',
      query: { redirect: to.fullPath }
    })
    return
  }
  
  // Verificar rutas de admin - SOLO Role_id === 1 puede acceder
  if (to.meta.requiresAdmin) {
    if (!userStore.isAuthenticated) {
      next({
        name: 'home',
        query: { redirect: to.fullPath }
      })
      return
    }
    
    // Verificar que el usuario sea admin (Role_id === 1)
    if (!userStore.isAdmin) {
      // Usuario autenticado pero NO es admin (Role_id !== 1)
      next({ name: 'home' })
      return
    }
  }
  
  // Establecer título de página
  document.title = (to.meta.title as string) || 'SupplemEarth | Premium Supplements'
  next()
})

export default router

